import React from "react";
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { Backdrop, SlideChip, SlideTitle, Card3D, Punchline, glow } from "./shared";
import { RainbowEAKC } from "../shared/RainbowEAKC";

const TOTAL = 18;

const fadeOut = (frame: number, durationInFrames: number) =>
  interpolate(frame, [durationInFrames - 24, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

const spAppear = (frame: number, fps: number, delay = 0) =>
  interpolate(spring({ frame: frame - delay, fps, config: SPRING.snappy }), [0, 1], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

/* ──────────────── Scene 13 — Pillar 5: Scoped MCP keys ──────────────── */
export const S13Pillar5: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const ap = spAppear(frame, fps);

  const KEYS = [
    { name: "claude-code-acme",  scopes: ["read:kb.acme", "tools:search"],          active: true,  c: COLORS.neonPurple },
    { name: "cursor-acme-dev",   scopes: ["read:kb.acme", "tools:search", "tools:generate"], active: true,  c: COLORS.neonCyan },
    { name: "legacy-acme-2025",  scopes: ["read:kb.acme"],                          active: false, c: COLORS.textMuted },
    { name: "partner-globex",    scopes: ["read:kb.globex.public"],                 active: true,  c: COLORS.neonGreen },
  ];

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonPurple} />
      <SlideChip n={13} total={TOTAL} color={COLORS.neonPurple} appear={ap} />
      <SlideTitle
        text="Pillar 5 · Scoped MCP keys"
        subtitle="Every agent key is tenant-scoped. Rate-limited. Revocable."
        color={COLORS.neonPurple}
        appear={ap}
      />

      <div style={{
        position: "absolute", top: 310, left: "50%", transform: "translateX(-50%)",
        width: 1500,
        display: "flex", flexDirection: "column", gap: 22,
      }}>
        {KEYS.map((k, i) => {
          const sp = spring({ frame: frame - 30 - i * 16, fps, config: SPRING.bouncy });
          const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const tx = interpolate(sp, [0, 1], [-80, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          return (
            <div key={k.name} style={{
              opacity: k.active ? op : op * 0.55,
              transform: `translateX(${tx}px) perspective(1200px) rotateX(2deg)`,
              height: 120,
              background: `linear-gradient(135deg, ${k.c}18, ${COLORS.bgCard}ee)`,
              border: `2px solid ${k.c}`,
              borderLeft: `6px solid ${k.c}`,
              borderRadius: 18,
              display: "flex",
              alignItems: "center",
              gap: 28,
              padding: "0 36px",
              boxShadow: `0 0 30px ${k.c}55`,
            }}>
              <div style={{ fontSize: 60 }}>{k.active ? "🔑" : "🗝️"}</div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 28,
                  fontFamily: FONTS.mono,
                  fontWeight: 800,
                  color: k.c,
                  textShadow: k.active ? glow(k.c) : "none",
                  textDecoration: k.active ? "none" : "line-through",
                }}>
                  {k.name}
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
                  {k.scopes.map((s, j) => (
                    <div key={j} style={{
                      fontSize: 14,
                      fontFamily: FONTS.mono,
                      color: k.active ? k.c : COLORS.textMuted,
                      padding: "4px 12px",
                      borderRadius: 8,
                      background: `${k.c}15`,
                      border: `1px solid ${k.c}55`,
                    }}>{s}</div>
                  ))}
                </div>
              </div>
              <div style={{
                fontSize: 16,
                fontFamily: FONTS.mono,
                color: k.active ? COLORS.neonGreen : COLORS.neonRed,
                padding: "8px 16px",
                borderRadius: 10,
                background: k.active ? `${COLORS.neonGreen}18` : `${COLORS.neonRed}18`,
                border: `1.5px solid ${k.active ? COLORS.neonGreen : COLORS.neonRed}`,
                fontWeight: 800,
                letterSpacing: 1,
                textTransform: "uppercase",
              }}>
                {k.active ? "● active" : "revoked"}
              </div>
            </div>
          );
        })}
      </div>

      <Punchline
        text={<>Leaked key? <span style={{ color: COLORS.neonPurple }}>One click.</span> Gone.</>}
        color={COLORS.textPrimary}
        appear={spAppear(frame, fps, 200)}
      />
    </AbsoluteFill>
  );
};

/* ──────────────── Scene 14 — Pillar 6: Strict Source Mode ──────────────── */
export const S14Pillar6: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const ap = spAppear(frame, fps);

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonPink} />
      <SlideChip n={14} total={TOTAL} color={COLORS.neonPink} appear={ap} />
      <SlideTitle
        text="Pillar 6 · Strict source mode"
        subtitle="LLM refuses to answer without a citation."
        color={COLORS.neonPink}
        appear={ap}
      />

      {/* Side-by-side: hallucinated answer vs EAKC answer */}
      <div style={{
        position: "absolute", top: 330, left: 0, right: 0,
        display: "flex", justifyContent: "center", gap: 60,
      }}>
        {[
          {
            title: "Typical LLM",
            subtitle: "Confident. Invented.",
            body: "Our policy allows 14-day refunds on enterprise plans, as stated in the standard MSA.",
            cites: [],
            c: COLORS.neonRed,
            delay: 20,
          },
          {
            title: "EAKC · Strict Source",
            subtitle: "Grounded or silent.",
            body: "I cannot answer without finding this in your knowledge base. The uploaded Acme MSA contains no refund clause — please confirm with Legal.",
            cites: ["acme-msa-v3.pdf (no match)", "legal-policies.md (no match)"],
            c: COLORS.neonGreen,
            delay: 50,
          },
        ].map((col) => (
          <Card3D key={col.title} inline w={720} h={540} color={col.c} frame={frame} delay={col.delay} fps={fps}>
            <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 18 }}>
              <div style={{
                fontSize: 32,
                fontFamily: FONTS.heading,
                fontWeight: 900,
                color: col.c,
                textShadow: glow(col.c, true),
                letterSpacing: -0.5,
              }}>
                {col.title}
              </div>
              <div style={{
                fontSize: 18,
                fontFamily: FONTS.mono,
                color: COLORS.textMuted,
                letterSpacing: 1,
                marginBottom: 4,
                textTransform: "uppercase",
              }}>
                {col.subtitle}
              </div>
              <div style={{
                fontSize: 22,
                fontFamily: FONTS.body,
                color: COLORS.textPrimary,
                lineHeight: 1.5,
                padding: "16px 0",
              }}>
                {col.body}
              </div>
              <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
                {col.cites.length === 0 ? (
                  <div style={{
                    fontSize: 14,
                    fontFamily: FONTS.mono,
                    color: col.c,
                    padding: "10px 16px",
                    background: `${col.c}15`,
                    borderRadius: 8,
                    border: `1px dashed ${col.c}`,
                  }}>⚠ No source · likely hallucination</div>
                ) : (
                  col.cites.map((c, i) => (
                    <div key={i} style={{
                      fontSize: 14,
                      fontFamily: FONTS.mono,
                      color: col.c,
                      padding: "10px 16px",
                      background: `${col.c}12`,
                      borderRadius: 8,
                      border: `1px solid ${col.c}77`,
                    }}>
                      [{i + 1}] {c}
                    </div>
                  ))
                )}
              </div>
            </div>
          </Card3D>
        ))}
      </div>
    </AbsoluteFill>
  );
};

/* ──────────────── Scene 15 — Pillar 7: Audit Logs ──────────────── */
export const S15Pillar7: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const ap = spAppear(frame, fps);

  const LOGS = [
    { t: "2026-04-13 09:41:22.102",  actor: "user:alice@acme",  action: "query",        detail: "'batch pipeline status'", c: COLORS.neonBlue },
    { t: "2026-04-13 09:41:22.198",  actor: "rls:engine",        action: "policy pass",  detail: "tenant=acme, rows=12",      c: COLORS.neonGreen },
    { t: "2026-04-13 09:41:22.301",  actor: "llm:gpt-4",         action: "generate",     detail: "model=router-analytical",   c: COLORS.neonPurple },
    { t: "2026-04-13 09:41:22.412",  actor: "audit",             action: "sources",       detail: "[batch_runner.py#L42..]",   c: COLORS.neonCyan },
    { t: "2026-04-13 09:41:22.515",  actor: "user:bob@globex",   action: "query",        detail: "'acme pricing details'",    c: COLORS.neonBlue },
    { t: "2026-04-13 09:41:22.520",  actor: "rls:engine",        action: "policy deny",  detail: "tenant=globex ≠ acme, 0",   c: COLORS.neonRed },
  ];

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonYellow} />
      <SlideChip n={15} total={TOTAL} color={COLORS.neonYellow} appear={ap} />
      <SlideTitle
        text="Pillar 7 · Every query audited"
        subtitle="Actor · action · source · timestamp — immutable, append-only."
        color={COLORS.neonYellow}
        appear={ap}
      />

      <div style={{
        position: "absolute", top: 320, left: "50%", transform: "translateX(-50%) perspective(1200px) rotateX(3deg)",
        width: 1600,
        background: COLORS.bgCardLight,
        border: `2.5px solid ${COLORS.neonYellow}`,
        borderRadius: 20,
        padding: "28px 36px",
        boxShadow: `0 0 40px ${COLORS.neonYellow}55, 0 20px 40px rgba(0,0,0,0.5)`,
      }}>
        <div style={{ fontSize: 14, fontFamily: FONTS.mono, color: COLORS.textMuted, marginBottom: 20, letterSpacing: 2, textTransform: "uppercase" }}>
          audit_log.jsonl · append-only · tamper-evident
        </div>

        {LOGS.map((l, i) => {
          const sp = spring({ frame: frame - 20 - i * 14, fps, config: SPRING.gentle });
          const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          return (
            <div key={i} style={{
              opacity: op,
              fontSize: 19,
              fontFamily: FONTS.mono,
              display: "flex",
              gap: 22,
              padding: "10px 0",
              borderBottom: i < LOGS.length - 1 ? `1px solid ${COLORS.neonYellow}22` : "none",
            }}>
              <span style={{ color: COLORS.textMuted, minWidth: 230 }}>{l.t}</span>
              <span style={{ color: COLORS.neonCyan, minWidth: 220 }}>{l.actor}</span>
              <span style={{ color: l.c, minWidth: 140, fontWeight: 800, textShadow: glow(l.c) }}>{l.action}</span>
              <span style={{ color: COLORS.textSecondary }}>{l.detail}</span>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

/* ──────────────── Scene 16 — Summary: 7-pillar grid ──────────────── */
export const S16Summary: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const ap = spAppear(frame, fps);

  const PILLARS = [
    { n: "01", icon: "🗄️", label: "Postgres RLS",        c: COLORS.neonBlue },
    { n: "02", icon: "🖥️", label: "Self-hosted",         c: COLORS.neonGreen },
    { n: "03", icon: "🧠", label: "Open embeddings",      c: COLORS.neonPurple },
    { n: "04", icon: "🔍", label: "Opt-in web",           c: COLORS.neonCyan },
    { n: "05", icon: "🔑", label: "Scoped MCP keys",      c: COLORS.neonPink },
    { n: "06", icon: "📑", label: "Strict sources",       c: COLORS.neonOrange },
    { n: "07", icon: "📜", label: "Audit logs",           c: COLORS.neonYellow },
  ];

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonCyan} />
      <SlideChip n={16} total={TOTAL} color={COLORS.neonCyan} appear={ap} />
      <SlideTitle
        text="Security bill of materials"
        subtitle="7 pillars — shipped in every EAKC deployment."
        color={COLORS.neonCyan}
        appear={ap}
      />

      <div style={{
        position: "absolute", top: 300, left: 0, right: 0,
        display: "grid",
        gridTemplateColumns: "repeat(4, 380px)",
        gap: 32,
        justifyContent: "center",
        perspective: 1400,
      }}>
        {PILLARS.map((p, i) => {
          const sp = spring({ frame: frame - 20 - i * 12, fps, config: SPRING.bouncy });
          const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const sc = interpolate(sp, [0, 1], [0.4, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const float = Math.sin(frame * 0.025 + i * 0.8) * 8;
          return (
            <div key={p.n} style={{
              width: 380, height: 240,
              opacity: op,
              transform: `scale(${sc}) translateY(${float}px) rotateY(${Math.sin(frame * 0.01 + i) * 4}deg)`,
              transformStyle: "preserve-3d",
            }}>
              <div style={{
                width: "100%", height: "100%",
                background: `linear-gradient(140deg, ${p.c}22, ${COLORS.bgCard}ee)`,
                border: `2.5px solid ${p.c}`,
                borderRadius: 22,
                padding: 24,
                boxShadow: `0 0 40px ${p.c}55, 0 0 80px ${p.c}22, 0 20px 40px rgba(0,0,0,0.5)`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}>
                <div style={{
                  fontSize: 18,
                  fontFamily: FONTS.mono,
                  color: p.c,
                  opacity: 0.6,
                  fontWeight: 900,
                  letterSpacing: 2,
                }}>{p.n}</div>
                <div style={{ fontSize: 82 }}>{p.icon}</div>
                <div style={{
                  fontSize: 26,
                  fontFamily: FONTS.heading,
                  fontWeight: 800,
                  color: p.c,
                  textAlign: "center",
                  textShadow: glow(p.c, true),
                  letterSpacing: -0.5,
                }}>{p.label}</div>
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

/* ──────────────── Scene 17 — Compliance Ready ──────────────── */
export const S17Compliance: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const ap = spAppear(frame, fps);

  const BADGES = [
    { label: "SOC 2 type II",     note: "Audit-path friendly", c: COLORS.neonBlue },
    { label: "GDPR",               note: "Data locality controls", c: COLORS.neonGreen },
    { label: "HIPAA-ready",        note: "Self-host in covered env", c: COLORS.neonPurple },
    { label: "ISO 27001",          note: "Full audit trail", c: COLORS.neonOrange },
    { label: "India DPDP 2023",    note: "ap-south-1 data residency", c: COLORS.neonPink },
  ];

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut(frame, durationInFrames), overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonGreen} />
      <SlideChip n={17} total={TOTAL} color={COLORS.neonGreen} appear={ap} />
      <SlideTitle
        text="Compliance-ready by design"
        subtitle="The architecture decisions your auditor wants to hear."
        color={COLORS.neonGreen}
        appear={ap}
      />

      <div style={{
        position: "absolute", top: 330, left: 0, right: 0,
        display: "flex", justifyContent: "center", gap: 28, flexWrap: "wrap",
        padding: "0 100px",
      }}>
        {BADGES.map((b, i) => {
          const sp = spring({ frame: frame - 30 - i * 15, fps, config: SPRING.bouncy });
          const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const sc = interpolate(sp, [0, 1], [0.4, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          return (
            <div key={b.label} style={{
              width: 320,
              height: 340,
              opacity: op,
              transform: `scale(${sc}) perspective(1200px) rotateY(${(i - 2) * 4}deg)`,
              background: `linear-gradient(140deg, ${b.c}22, ${COLORS.bgCard}ee)`,
              border: `2.5px solid ${b.c}`,
              borderRadius: 22,
              padding: 26,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 18,
              boxShadow: `0 0 40px ${b.c}55, 0 20px 40px rgba(0,0,0,0.5)`,
            }}>
              <div style={{
                width: 120, height: 120,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${b.c}66, ${b.c}15 60%, transparent)`,
                border: `3px solid ${b.c}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 60,
                boxShadow: `0 0 30px ${b.c}88`,
              }}>✓</div>
              <div style={{
                fontSize: 24,
                fontFamily: FONTS.heading,
                fontWeight: 900,
                color: b.c,
                textShadow: glow(b.c, true),
                textAlign: "center",
                letterSpacing: -0.5,
                lineHeight: 1.15,
              }}>
                {b.label}
              </div>
              <div style={{
                fontSize: 16,
                fontFamily: FONTS.body,
                color: COLORS.textSecondary,
                textAlign: "center",
                lineHeight: 1.35,
              }}>
                {b.note}
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

/* ──────────────── Scene 18 — Outro CTA ──────────────── */
export const S18Outro: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleSp = spring({ frame, fps, config: SPRING.bouncy });
  const titleOp = interpolate(titleSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const titleScale = interpolate(titleSp, [0, 1], [0.4, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  const taglineOp = interpolate(spring({ frame: frame - 30, fps, config: SPRING.gentle }), [0, 1], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });
  const urlOp = interpolate(spring({ frame: frame - 60, fps, config: SPRING.gentle }), [0, 1], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  const urlPulse = interpolate(Math.sin(frame * 0.08), [-1, 1], [15, 40]);

  return (
    <AbsoluteFill style={{ background: COLORS.bg, overflow: "hidden" }}>
      <Backdrop frame={frame} accent={COLORS.neonPurple} />

      <div style={{
        position: "absolute", inset: 0,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: 28,
      }}>
        <RainbowEAKC
          id="sec-outro"
          frame={frame}
          fontSize={220}
          fontFamily={FONTS.heading}
          scale={titleScale}
          opacity={titleOp}
          glowColor={COLORS.neonPurple}
        />

        <div style={{
          fontSize: 48,
          fontFamily: FONTS.heading,
          fontWeight: 700,
          color: COLORS.neonCyan,
          textShadow: `0 0 40px ${COLORS.neonCyan}cc`,
          opacity: taglineOp,
          letterSpacing: 2,
          marginTop: 10,
          textAlign: "center",
        }}>
          Secure. Sourced. <span style={{ color: COLORS.neonGreen }}>Yours.</span>
        </div>

        <div style={{
          fontSize: 30,
          fontFamily: FONTS.mono,
          color: COLORS.neonCyan,
          opacity: urlOp,
          padding: "18px 38px",
          borderRadius: 50,
          background: `${COLORS.neonCyan}08`,
          border: `2px solid ${COLORS.neonCyan}77`,
          textShadow: `0 0 ${urlPulse}px ${COLORS.neonCyan}cc`,
          marginTop: 16,
        }}>
          eakc.adaptivesmartsystems.cc
        </div>

        <div style={{
          fontSize: 22,
          fontFamily: FONTS.body,
          color: COLORS.textMuted,
          opacity: urlOp,
          marginTop: 14,
          letterSpacing: 1,
        }}>
          Book a compliance review · Self-host in 15 minutes
        </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
